/**
 * MentionCard - Clean & luxe mention display
 * Matches the SERP style: flat rows, subtle hover, sentiment dot
 */
import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { colors } from '../../styles/colors';

const PLATFORM_CONFIGS = {
  twitter:        { color: '#1DA1F2', icon: 'X',  label: 'Twitter/X' },
  facebook:       { color: '#1877F2', icon: 'FB', label: 'Facebook' },
  instagram:      { color: '#E4405F', icon: 'IG', label: 'Instagram' },
  youtube:        { color: '#FF0000', icon: 'YT', label: 'YouTube' },
  tiktok:         { color: '#000000', icon: 'TT', label: 'TikTok' },
  linkedin:       { color: '#0A66C2', icon: 'LI', label: 'LinkedIn' },
  google_maps:    { color: '#4285F4', icon: 'GM', label: 'Google Maps' },
  google_reviews: { color: '#4285F4', icon: 'GR', label: 'Google' },
  news:           { color: '#6366F1', icon: 'N',  label: 'Actualites' },
  rss:            { color: '#F59E0B', icon: 'RS', label: 'RSS' },
  web:            { color: '#22C55E', icon: 'W',  label: 'Web' },
};

export { PLATFORM_CONFIGS };

/**
 * Highlight brand keywords in mention text
 */
const highlightKeywords = (text, keywords, dark = false) => {
  if (!keywords || keywords.length === 0) return text;
  const escaped = keywords.map(kw => kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(`(${escaped.join('|')})`, 'gi');
  const parts = text.split(regex);
  const highlightBg = dark ? 'rgba(52,211,153,0.2)' : `${colors.accent}15`;
  const highlightColor = dark ? 'rgba(250,247,242,0.95)' : undefined;
  return parts.map((part, i) =>
    regex.test(part)
      ? <Box component="span" key={i} sx={{ bgcolor: highlightBg, color: highlightColor, px: 0.3, borderRadius: 0.5, fontWeight: 600 }}>{part}</Box>
      : part
  );
};

/**
 * Format date to relative time in French
 */
const formatRelativeDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) return "A l'instant";
  if (diffMinutes < 60) return `Il y a ${diffMinutes} min`;
  if (diffHours < 24) return `Il y a ${diffHours}h`;
  if (diffDays < 7) return `Il y a ${diffDays}j`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const getSentimentColor = (sentiment) => {
  switch (sentiment) {
    case 'positive': return colors.success;
    case 'negative': return colors.danger;
    default: return colors.gray400;
  }
};

/**
 * Build a search/profile URL when no direct link is available
 */
const buildFallbackUrl = (platform, authorName, authorUsername, text) => {
  if (platform === 'linkedin') {
    if (authorUsername) return `https://www.linkedin.com/in/${encodeURIComponent(authorUsername)}`;
    if (authorName) return `https://www.linkedin.com/search/results/content/?keywords=${encodeURIComponent(authorName)}`;
  } else if (platform === 'twitter') {
    if (authorUsername) return `https://x.com/${authorUsername.replace(/^@/, '')}`;
    if (authorName) return `https://x.com/search?q=${encodeURIComponent(authorName)}`;
  } else if (platform === 'facebook') {
    if (authorName) return `https://www.facebook.com/search/posts/?q=${encodeURIComponent(authorName)}`;
  } else if (platform === 'youtube') {
    const snippet = (text || authorName || '').substring(0, 60);
    if (snippet) return `https://www.youtube.com/results?search_query=${encodeURIComponent(snippet)}`;
  } else if (platform === 'news' || platform === 'web' || platform === 'rss') {
    const snippet = (text || '').substring(0, 80);
    if (snippet) return `https://www.google.com/search?q=${encodeURIComponent(snippet)}`;
  }
  return '';
};

/**
 * MentionCard — Clean SERP-style layout
 * Flat row with author, platform tag, text, sentiment dot, link
 */
function MentionCard({ mention, brandKeywords, compact = false, dark = false, onClick }) {
  const [expanded, setExpanded] = useState(false);

  if (!mention) return null;

  const platform = mention.platform?.toLowerCase() || 'web';
  const platformConfig = PLATFORM_CONFIGS[platform] || { color: colors.gray500, icon: '?', label: platform };
  const sentimentColor = getSentimentColor(mention.sentiment);
  const text = mention.text || '';
  const isLongText = text.length > 200;

  const mentionUrl = mention.url || buildFallbackUrl(platform, mention.author_name, mention.author_username, text);

  const authorName = mention.author_name || mention.source || 'Inconnu';
  const relDate = formatRelativeDate(mention.published_at || mention.created_at);

  const displayText = compact
    ? text
    : (expanded || !isLongText)
      ? text
      : text.substring(0, 200) + '...';

  const handleClick = (e) => {
    if (e.target.closest('button') || e.target.closest('a')) return;
    if (mentionUrl) {
      window.open(mentionUrl, '_blank');
    } else if (onClick) {
      onClick(mention);
    }
  };

  // Dark-mode color tokens (for use inside dark-themed containers like ComprendrePage)
  const authorColor = dark ? 'rgba(250,247,242,0.95)' : colors.charcoal;
  const dateColor = dark ? 'rgba(250,247,242,0.5)' : colors.textLight;
  const snippetColor = dark ? 'rgba(250,247,242,0.7)' : colors.gray600;
  const hoverBg = dark ? 'rgba(255,255,255,0.06)' : colors.emeraldGlass;
  const accentColor = dark ? '#34D399' : colors.accent;

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1,
        py: 1, px: 1.5,
        borderRadius: '8px',
        cursor: 'pointer',
        textDecoration: 'none',
        transition: 'background-color 0.15s',
        '&:hover': { bgcolor: hoverBg },
      }}
    >
      {/* Platform icon — small colored tag */}
      <Typography sx={{
        fontSize: '0.6rem', fontFamily: '"DM Sans", sans-serif', fontWeight: 700,
        color: platformConfig.color, bgcolor: `${platformConfig.color}${dark ? '25' : '12'}`,
        px: 0.5, py: 0.2, borderRadius: '4px', mt: '2px',
        minWidth: 22, textAlign: 'center', flexShrink: 0,
        letterSpacing: '0.02em',
      }}>
        {platformConfig.icon}
      </Typography>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Author + date */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.25 }}>
          <Typography sx={{
            fontSize: '0.78rem', fontFamily: '"DM Sans", sans-serif', fontWeight: 600,
            color: authorColor, lineHeight: 1.3,
          }} noWrap>
            {authorName}
          </Typography>
          <Typography sx={{
            fontSize: '0.62rem', fontFamily: '"DM Sans", sans-serif',
            color: dateColor, whiteSpace: 'nowrap',
          }}>
            {relDate}
          </Typography>
        </Box>
        {/* Text */}
        <Typography sx={{
          fontSize: '0.75rem', fontFamily: '"DM Sans", sans-serif',
          color: snippetColor, lineHeight: 1.5,
          overflow: 'hidden', textOverflow: 'ellipsis',
          ...(!expanded && { display: '-webkit-box', WebkitLineClamp: compact ? 2 : 3, WebkitBoxOrient: 'vertical' }),
        }}>
          {brandKeywords ? highlightKeywords(displayText, brandKeywords, dark) : displayText}
        </Typography>
        {!compact && isLongText && (
          <Typography
            component="span"
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
            sx={{
              fontSize: '0.68rem', color: accentColor, cursor: 'pointer',
              fontFamily: '"DM Sans", sans-serif', fontWeight: 500,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {expanded ? 'Voir moins' : 'Voir plus'}
          </Typography>
        )}
      </Box>

      {/* Sentiment dot */}
      <Box sx={{
        width: 6, height: 6, borderRadius: '50%', mt: '6px', flexShrink: 0,
        bgcolor: sentimentColor,
      }} />
    </Box>
  );
}

export default MentionCard;
